import {
  isUnplannedTables,
  sectionFailureSentence,
} from '@/modules/projects/components/portfolio/describeSectionFailure'
import type {
  PortfolioPeriod,
  SectionValidation,
} from '@/modules/projects/components/portfolio/types'
import {
  isOnlyLastPageMissing,
  missingPagesLine,
} from '@/modules/projects/components/tables/statements/coverage'
import { NUMBER_MASK } from '@/modules/projects/components/Workbench/accounts'
import { numberToCurrency } from '@/utils'
import { monthLabel, monthShortLabel } from './months'
import type { BadgeReason, MonthKey, ReasonAction } from './types'

const openStatement = (
  statement: { statementFileUrl: string },
  month: MonthKey,
): ReasonAction => ({
  kind: 'open_statement',
  label: `Open the ${monthLabel(month)} statement`,
  fileUrl: statement.statementFileUrl,
})

const uploadFull = (month: MonthKey): ReasonAction => ({
  kind: 'upload_statement',
  label: `Upload the full ${monthShortLabel(month)} statement`,
  month,
})

// No total is involved: the page prints a table the plan does not cover, so its rows may be missing.
const tableNotExtractedReason = (
  period: PortfolioPeriod,
  month: MonthKey,
): BadgeReason => ({
  code: 'table_not_extracted',
  month,
  sentence: `The ${monthLabel(month)} statement has a table that was not extracted.`,
  action: openStatement(period, month),
})

const totalCheckReason = (
  period: PortfolioPeriod,
  month: MonthKey,
  section: SectionValidation,
): BadgeReason => ({
  code: section.anchorMissing ? 'unreadable_total' : 'failed_check',
  month,
  sentence: sectionFailureSentence(section),
  action: openStatement(period, month),
})

const checkReasons = (
  period: PortfolioPeriod,
  month: MonthKey,
): BadgeReason[] =>
  period.failingSections.map((section) =>
    isUnplannedTables(section)
      ? tableNotExtractedReason(period, month)
      : totalCheckReason(period, month, section),
  )

const readReasons = (
  period: PortfolioPeriod,
  month: MonthKey,
): BadgeReason[] => {
  const reasons: BadgeReason[] = []
  if (period.droppedRowReads > 0) {
    reasons.push({
      code: 'rows_dropped',
      month,
      sentence: `Some rows on the ${monthLabel(month)} statement could not be read.`,
      action: openStatement(period, month),
    })
  }
  if (period.holdingsOpeningMissing) {
    reasons.push({
      code: 'opening_page_missing',
      month,
      sentence: `The ${monthLabel(month)} statement is missing its opening holdings page.`,
      action: uploadFull(month),
    })
  }
  const pagesLine = missingPagesLine(period.printedPages)
  if (pagesLine) {
    reasons.push({
      code: 'pages_missing',
      month,
      sentence: pagesLine,
      action: uploadFull(month),
      lastPageOnly: isOnlyLastPageMissing(period.printedPages),
    })
  }
  return reasons
}

const cashReason = (period: PortfolioPeriod, month: MonthKey): BadgeReason[] =>
  period.cashGap == null
    ? []
    : [
        {
          code: 'cash_gap',
          month,
          sentence: `${monthLabel(month)} cash is ${numberToCurrency(Math.abs(period.cashGap))} off the printed balance.`,
          action: openStatement(period, month),
        },
      ]

// The engine said no but named nothing we can read. Still amber, never a silent Verified.
const notCheckedReason = (
  period: PortfolioPeriod,
  month: MonthKey,
): BadgeReason => ({
  code: 'not_checked',
  month,
  sentence:
    period.printedTotals.length === 0
      ? `The ${monthLabel(month)} statement prints no total to check the holdings against.`
      : `The ${monthLabel(month)} statement did not pass its checks.`,
  action: openStatement(period, month),
})

// Every reason one statement gives to turn its account amber.
export const statementReasons = (
  period: PortfolioPeriod,
  month: MonthKey,
): BadgeReason[] => {
  const reasons = [
    ...checkReasons(period, month),
    ...readReasons(period, month),
    ...cashReason(period, month),
  ]
  if (reasons.length === 0 && !period.validated) {
    return [notCheckedReason(period, month)]
  }
  return reasons
}

// One reason per run of missing months, so a year without statements is one line, not twelve.
export const missingStatementReason = (run: MonthKey[]): BadgeReason => {
  const first = run[0]
  const last = run[run.length - 1]
  if (run.length === 1) {
    return {
      code: 'missing_statement',
      month: first,
      sentence: `No ${monthLabel(first)} statement.`,
      action: {
        kind: 'upload_statement',
        label: `Upload the ${monthShortLabel(first)} statement`,
        month: first,
      },
      missingMonths: run,
    }
  }
  return {
    code: 'missing_statement',
    month: last,
    sentence: `No statements from ${monthLabel(first)} to ${monthLabel(last)}.`,
    action: {
      kind: 'upload_statement',
      label: 'Upload the missing statements',
      month: first,
    },
    missingMonths: run,
  }
}

// Two statements end in one month, so neither value is kept. One button opens each file.
export const twoStatementsReasons = (
  month: MonthKey,
  statements: { statementFileUrl: string }[],
): BadgeReason[] =>
  statements.map((statement, position) => ({
    code: 'two_statements',
    month,
    sentence: `${statements.length === 2 ? 'Two' : statements.length} statements for ${monthLabel(month)}.`,
    action: {
      kind: 'open_statement',
      label: `Open statement ${position + 1} of ${statements.length}`,
      fileUrl: statement.statementFileUrl,
    },
  }))

// The customer uploaded this file but its securities could not be read, so the button opens it.
export const failedReadReason = (
  statement: { statementFileUrl: string },
  month: MonthKey,
): BadgeReason => ({
  code: 'failed_read',
  month,
  sentence: `The ${monthLabel(month)} statement could not be read.`,
  action: openStatement(statement, month),
})

// The read worked for the other accounts on the statement but returned none of this one.
export const accountNotReadReason = (
  statement: { statementFileUrl: string },
  month: MonthKey,
  last4: string,
): BadgeReason => ({
  code: 'account_not_read',
  month,
  sentence: `The ${monthLabel(month)} statement was read without account ${NUMBER_MASK} ${last4}.`,
  action: openStatement(statement, month),
})

const UNREAD_CODES = new Set(['failed_read', 'account_not_read'])

// A statement with no securities for this account: the read failed, or left the account out.
export const isFailedRead = (statement: { reasons: BadgeReason[] }) =>
  statement.reasons.some((reason) => UNREAD_CODES.has(reason.code))

// Another brokerage reports the same last 4 for a month this statement covers.
export const twoInstitutionsReason = (
  statement: { statementFileUrl: string },
  month: MonthKey,
  sharedMonth: MonthKey,
  last4: string,
): BadgeReason => ({
  code: 'two_institutions',
  month,
  sentence: `Two institutions report account ${NUMBER_MASK} ${last4} for ${monthLabel(sharedMonth)}.`,
  action: openStatement(statement, month),
})
